import React, { useContext, useEffect, useState } from "react";
import { secured } from "react-abac";
import moment from "moment";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Button, Card, Grid, Icon, InputText, InputToggle, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import InputTextArea from "~/components-shared/InputTextArea";
import api from "~/lib/api";
import { handleError, normalizeData } from "~/lib/helpers";
import useAbacExt from "~/hooks/abac";
import styled, { useTheme } from "styled-components";
import toastsActionCreators from "~/store/toasts/actions";
import { useDispatch } from "react-redux";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const AiPrompts = () => {
  const {
    t
  } = useTranslation();
  const {
    colors
  } = useTheme();
  const {
    setSidebar
  } = useContext(CoreContext);
  const [prompts, setPrompts] = useState({});
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState();
  const [error, setError] = useState();
  const [filter, setFilter] = useState({
    query: ""
  });
  const [diffView, setDiffView] = useState([]);
  const [open, setOpen] = useState([]);
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    setError(undefined);
    setLoading(true);
    api.fetchAiPrompts({
      filter
    }).then(data => setPrompts(normalizeData(data).entities)).catch(e => setError(handleError(e))).finally(() => setLoading(false));
  }, [filter.query]);
  const handleSubmit = (id, text) => {
    setError(undefined);
    setCardLoading(id);
    api.updateAiPrompt(id, text).then(newPrompt => {
      prompts[newPrompt.id] = newPrompt;
      setPrompts(prompts);
      dispatch(toastsActionCreators.pushed({
        severity: "success",
        message: `Prompt **${newPrompt.title}** successfully updated`,
        timeout: 10000
      }));
    }).catch(e => setError(handleError(e))).finally(() => setCardLoading(undefined));
  };
  const handleToggleStatus = id => {
    setError(undefined);
    setCardLoading(id);
    api.updateAiPromptStatus(id, !prompts[id].isActive).then(newPrompt => {
      prompts[newPrompt.id].isActive = newPrompt.isActive;
      prompts[newPrompt.id].updatedAt = newPrompt.updatedAt;
      setPrompts(prompts);
      dispatch(toastsActionCreators.pushed({
        severity: "success",
        message: `Prompt **${newPrompt.title}** status successfully updated`,
        timeout: 10000
      }));
    }).catch(e => setError(handleError(e))).finally(() => setCardLoading(undefined));
  };
  const handleChange = (text, prompt) => {
    setPrompts(prev => ({
      ...prev,
      [prompt.id]: {
        ...prompt,
        text
      }
    }));
  };
  const handleFilter = value => {
    setFilter(value);
  };
  const handleOpen = id => {
    if (open.includes(id)) {
      setOpen(open.filter(item => item !== id));
    } else {
      setOpen([...open, id]);
    }
  };
  const handleDiff = id => {
    if (diffView.includes(id)) {
      setDiffView(diffView.filter(item => item !== id));
    } else {
      setDiffView([...diffView, id]);
    }
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mt: 5,
      mb: 8
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("AiPrompts.aiPrompts")), /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("AiPrompts.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    clearButton: true,
    s: {
      mb: 8
    }
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 6
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      my: 6
    },
    center: true
  }) : Object.values(prompts).map(prompt => /*#__PURE__*/React.createElement(Card, {
    elemBefore: /*#__PURE__*/React.createElement(Text, {
      element: "h2"
    }, prompt.title),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      s: {
        mr: 3
      },
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      s: {
        mr: 1,
        mt: -0.5
      }
    }), moment(prompt.updatedAt).format("ddd, MMMM D ⋅ hh:mma")), /*#__PURE__*/React.createElement(InputToggle, {
      value: prompt.isActive,
      display: "inline-block",
      onChange: (_, event) => {
        event.stopPropagation();
        handleToggleStatus(prompt.id);
      },
      disabled: !userHas(AuthoritiesEnum.INTEGRATIONS_WRITE)
    })),
    shadow: true,
    color: "background",
    key: prompt.id,
    onClick: () => handleOpen(prompt.id),
    collapsed: !open.includes(prompt.id),
    loading: cardLoading === prompt.id
  }, /*#__PURE__*/React.createElement("form", {
    style: {
      zIndex: 2,
      position: "relative"
    },
    onClick: event => event.stopPropagation(),
    onSubmit: () => handleSubmit(prompt.id, prompt.text)
  }, !!prompt.variables?.length && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    s: {
      mt: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, "Mandatory variables:"), prompt.variables.map(variable => /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      ml: 2
    }
  }, `{{${variable}}}`))), /*#__PURE__*/React.createElement(InputTextArea, {
    name: "text",
    value: prompt.text,
    onChange: text => handleChange(text, prompt),
    s: {
      my: 3
    }
  }), /*#__PURE__*/React.createElement(Card, {
    s: {
      mb: 3
    },
    title: /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold"
    }, "Show core vs custom difference"),
    onClick: () => handleDiff(prompt.id),
    collapsed: !diffView.includes(prompt.id),
    border: true
  }, /*#__PURE__*/React.createElement(ReactDiffViewer, {
    oldValue: prompt.original,
    newValue: prompt.text,
    showDiffOnly: true,
    splitView: true,
    compareMethod: DiffMethod.WORDS,
    hideLineNumbers: true
  })), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: () => handleSubmit(prompt.id, prompt.text),
    s: {
      px: 8,
      py: 3
    },
    loading: cardLoading === prompt.id
  }, t("common.actions.save")))))));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.AI_PROMPTS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(AiPrompts), () => true);